import { useState, useRef, type InputHTMLAttributes } from "react";
import { Input } from "../ui/input";
import CheckIcon from "../../assets/svgs/common/check.svg";
import PenIcon from "../../assets/svgs/common/pen.svg";

interface InputWithCheckProps extends InputHTMLAttributes<HTMLInputElement> {
  id?: string;
  defaultValue: number | string;
  onUpdate: (newValue: number) => void;
}

const InputWithCheck = ({
  id,
  defaultValue,
  onUpdate,
  ...inputProps
}: InputWithCheckProps) => {
  const [editing, setEditing] = useState<boolean>(false);
  const initialValue =
    typeof defaultValue === "string"
      ? Number.parseFloat(defaultValue)
      : defaultValue;
  const [currentValue, setCurrentValue] = useState<number>(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleUpdate = (newValue: number) => {
    onUpdate(newValue);
    setEditing(false);
  };

  return (
    <div className="flex items-center space-x-2">
      <Input
        type="number"
        min={0}
        id={id}
        defaultValue={defaultValue}
        className="bg-transparent border-none w-20"
        disabled={!editing}
        ref={inputRef}
        onBlur={() => setEditing(false)}
        onChange={(e) => setCurrentValue(Number(e.target.value))}
        {...inputProps}
      />
      {/* Prevent input from losing focus when clicking the icon */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <div
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          if (editing) {
            if (currentValue !== initialValue) {
              handleUpdate(currentValue);
            } else {
              setEditing(false);
            }
          } else {
            setEditing(true);
            setTimeout(() => {
              inputRef.current?.focus();
            }, 0);
          }
        }}
        className="cursor-pointer"
      >
        {editing ? (
          <CheckIcon
            className={`w-5 h-5 ${currentValue === initialValue ? "opacity-50" : "opacity-100"}`}
          />
        ) : (
          <PenIcon />
        )}
      </div>
    </div>
  );
};

export default InputWithCheck;
